/**
 * CAOS-752 — whether the edge rail draws its tabs as marks only, without their words.
 *
 * Its own module because two surfaces need it and neither should have to import the other: the rail
 * (`surfaces/shell-frame.ts`) obeys it, and the control that sets it is in My Profile › Preferences
 * (`surfaces/profile.ts`), which is a different surface reached by a different route.
 *
 * A PLATFORM preference (`rail.compact`), sitting exactly where `rail.pinned.<source>` already does:
 * the rail is the workspace's own furniture, the platform is what draws it, and every other choice
 * about it is kept against the person the same way.
 *
 * It is deliberately NOT a package-contributed setting on the `profile-preferences` seam, which is
 * how CAOS-752 first described it. A package cannot deploy a claim carrying a field newer than the
 * kernel release its repository validates against, and that seam's descriptor field is newer than
 * any published release — so the seam route cannot put this setting in anybody's hands yet. The
 * declaration is written (caos-pkg-core-shell PR #12) and is the migration path the day a release
 * carries it; what a person sees does not change when it moves.
 *
 * WHOLE-RAIL, not per tab. Every tab shares one vertical lane, so a rail with some tabs worded and
 * some not reads as half-broken rather than as a choice. `rail.pinned.<source>` is per tab because
 * tucking ONE tab away is a thing a person means to do; compactness is a property of the rail.
 */
import { preference, setPreference } from './person-preferences.js';

/** The stored key. The kernel's closed vocabulary names this one too — a drift is refused there. */
export const COMPACT_RAIL_KEY = 'rail.compact';

/** Whether the rail is in marks-only mode. Anything but an explicit `true` leaves the words on. */
export function compactRailOn(): boolean {
  return preference(COMPACT_RAIL_KEY) === true;
}

/**
 * Choose marks-only mode. Nothing is redrawn here: the store announces the key it settles on — at
 * once, optimistically, and again if the kernel refuses — and every control and the rail itself
 * listen for that. So a change made on another device arrives the same way a press does, and a
 * refused press does not leave one of them showing a choice that was never stored.
 */
export function setCompactRail(on: boolean): void {
  void setPreference(COMPACT_RAIL_KEY, on);
}
